import { isSandboxMode } from "./sandbox";

// Where the app token is cached between page loads.
//
// A logged-in user and a sandbox visitor share one origin (app.insaion.com),
// so they must not share one cache slot: opening /sandbox from a logged-in tab
// used to overwrite the real user's cached token, and a reload of the original
// tab then landed in the demo session. Sandbox tokens therefore live in
// sessionStorage under their own key — scoped to the tab that minted them —
// while the OIDC user's token keeps the origin-wide localStorage slot.
//
// NOTE: like lib/sandbox.ts, this module must not import lib/api.ts.

const USER_TOKEN_KEY = "appToken";
const SANDBOX_TOKEN_KEY = "sandboxAppToken";

/** Cached token for the session this tab belongs to, if any. */
export function readCachedAppToken(): string | null {
  try {
    return isSandboxMode()
      ? sessionStorage.getItem(SANDBOX_TOKEN_KEY)
      : localStorage.getItem(USER_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Caches the token so a hard refresh doesn't lose it before the session is
 * re-hydrated (from the OIDC context, or from a re-minted sandbox session).
 */
export function cacheAppToken(token: string): void {
  try {
    if (isSandboxMode()) {
      sessionStorage.setItem(SANDBOX_TOKEN_KEY, token);
    } else {
      localStorage.setItem(USER_TOKEN_KEY, token);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Drops only the sandbox tab's cached token. Ending a demo must not evict a
 * logged-in user's token, which is cached origin-wide.
 */
export function clearCachedSandboxToken(): void {
  try {
    sessionStorage.removeItem(SANDBOX_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Drops every cached token, whichever kind of session wrote it. */
export function clearCachedAppToken(): void {
  try {
    localStorage.removeItem(USER_TOKEN_KEY);
    sessionStorage.removeItem(USER_TOKEN_KEY);
    sessionStorage.removeItem(SANDBOX_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
